import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { calculateFinalPayableRounding } from "../runtime/commerce/final-payable-rounding";
import {
  localCartSaleFingerprint,
  readLocalCart,
  type LocalCartDraft,
} from "../runtime/local-store/cart-state";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import {
  discardLocalParkedCart,
  listLocalParkedCarts,
  parkCurrentAndResumeLocalCart,
  parkCurrentLocalCart,
  resumeLocalParkedCart,
  type LocalParkedCartDraft,
} from "../runtime/local-store/parked-cart-state";
import {
  readTerminalCredential,
  type TerminalCredentialState,
} from "../runtime/local-store/terminal-state";
import "./ParkedCartRuntimeHost.css";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
  cart: LocalCartDraft | null;
  parked: LocalParkedCartDraft[];
};

function formatMoney(value: number, currencyCode = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatParkedTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
}

function cartItemCount(cart: LocalCartDraft) {
  return cart.lines.reduce((sum, line) => sum + line.quantity, 0);
}

function activeQuote(cart: LocalCartDraft) {
  return cart.quote && cart.quote.cartFingerprint === localCartSaleFingerprint(cart)
    ? cart.quote
    : null;
}

function cartTotal(cart: LocalCartDraft) {
  const subtotal = cart.lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
  const preRoundingTotal = activeQuote(cart)?.postPromotionTotal ?? subtotal;
  return cart.commerceCalculation
    ? calculateFinalPayableRounding(preRoundingTotal, cart.commerceCalculation).totalAmount
    : preRoundingTotal;
}

function cartCurrency(cart: LocalCartDraft) {
  return activeQuote(cart)?.currencyCode ?? cart.lines[0]?.currencyCode ?? "THB";
}

function errorText(value: unknown) {
  const message = value instanceof Error ? value.message : String(value ?? "");
  if (message.includes("ACTIVE_CART_EMPTY")) return "บิลปัจจุบันยังไม่มีสินค้าให้พัก";
  if (message.includes("ACTIVE_CART_NOT_EMPTY")) return "บิลปัจจุบันยังมีสินค้าอยู่ ต้องพักบิลปัจจุบันก่อน";
  if (message.includes("PARKED_CART_NOT_FOUND")) return "ไม่พบบิลพักนี้แล้ว กรุณาเปิดรายการบิลพักใหม่";
  return message || "ทำรายการบิลพักไม่สำเร็จ";
}

export function ParkedCartRuntimeHost() {
  const [snapshot, setSnapshot] = useState<RuntimeSnapshot>({
    terminal: null,
    session: null,
    cart: null,
    parked: [],
  });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumeCandidate, setResumeCandidate] = useState<LocalParkedCartDraft | null>(null);
  const [discardCandidate, setDiscardCandidate] = useState<LocalParkedCartDraft | null>(null);

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const [terminal, session] = await Promise.all([
        readTerminalCredential(),
        readLocalEmployeeSession(),
      ]);
      const identity = terminal?.identity ?? null;
      const [cart, parked] = identity
        ? await Promise.all([readLocalCart(identity), listLocalParkedCarts(identity)])
        : [null, [] as LocalParkedCartDraft[]];
      return { terminal, session, cart, parked };
    }).subscribe({
      next: setSnapshot,
      error: () => setSnapshot({ terminal: null, session: null, cart: null, parked: [] }),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-cart-header"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const identity = snapshot.terminal?.identity ?? null;
  const actor = snapshot.session?.actor ?? null;
  const cart = snapshot.cart;
  const parked = snapshot.parked;
  const sameScope = Boolean(
    identity &&
      actor &&
      actor.runtime_session_status === "ACTIVE" &&
      actor.organization_id === identity.organization_id &&
      actor.branch_id === identity.branch_id &&
      actor.node_id === identity.node_id,
  );
  const activeItemCount = cart ? cartItemCount(cart) : 0;

  const oldestParkedMinutes = useMemo(() => {
    if (!parked.length) return null;
    const first = new Date(parked[0].parkedAt).getTime();
    if (!Number.isFinite(first)) return null;
    return Math.max(0, Math.floor((Date.now() - first) / 60_000));
  }, [parked]);

  async function parkCurrent() {
    if (!identity || !actor || !cart?.lines.length) return;
    setBusy(true);
    setError(null);
    try {
      await parkCurrentLocalCart(identity, actor);
    } catch (value) {
      setError(errorText(value));
    } finally {
      setBusy(false);
    }
  }

  async function resumeParked(candidate: LocalParkedCartDraft) {
    if (!identity || !actor || !cart) return;
    if (cart.lines.length) {
      setResumeCandidate(candidate);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await resumeLocalParkedCart(identity, candidate.parkedCartId);
      setOpen(false);
    } catch (value) {
      setError(errorText(value));
    } finally {
      setBusy(false);
    }
  }

  async function confirmSwap() {
    if (!identity || !actor || !resumeCandidate) return;
    setBusy(true);
    setError(null);
    try {
      await parkCurrentAndResumeLocalCart(identity, actor, resumeCandidate.parkedCartId);
      setResumeCandidate(null);
      setOpen(false);
    } catch (value) {
      setError(errorText(value));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDiscard() {
    if (!identity || !discardCandidate) return;
    setBusy(true);
    setError(null);
    try {
      await discardLocalParkedCart(identity, discardCandidate.parkedCartId);
      setDiscardCandidate(null);
      if (parked.length <= 1) setOpen(false);
    } catch (value) {
      setError(errorText(value));
    } finally {
      setBusy(false);
    }
  }

  if (!mountNode || !sameScope || !cart) return null;

  const actions = createPortal(
    <div className="parked-cart-actions" aria-label="การพักบิล">
      {parked.length ? (
        <button
          className="parked-cart-button parked-cart-button--list"
          type="button"
          disabled={busy}
          onClick={() => { setError(null); setOpen(true); }}
        >
          บิลพัก ({parked.length})
        </button>
      ) : null}
      <button
        className="parked-cart-button"
        type="button"
        disabled={busy || activeItemCount === 0}
        onClick={() => void parkCurrent()}
      >
        พักบิล
      </button>
    </div>,
    mountNode,
  );

  if (!open) return actions;

  const modal = createPortal(
    <div className="pos-modal-backdrop parked-cart-backdrop" role="presentation">
      <section className="pos-modal parked-cart-modal" role="dialog" aria-modal="true" aria-labelledby="parked-cart-title">
        <div className="pos-modal__header parked-cart-modal__header">
          <div>
            <p className="eyebrow">LOCAL PARKED CARTS</p>
            <h2 id="parked-cart-title">บิลพัก ({parked.length})</h2>
            <p>
              เครื่องนี้เท่านั้น
              {oldestParkedMinutes !== null ? ` · บิลเก่าสุดพักมา ${oldestParkedMinutes} นาที` : ""}
            </p>
          </div>
          <button
            type="button"
            className="pos-modal__close"
            disabled={busy}
            onClick={() => { setOpen(false); setResumeCandidate(null); setDiscardCandidate(null); setError(null); }}
            aria-label="ปิดรายการบิลพัก"
          >
            ×
          </button>
        </div>

        {error ? <div className="parked-cart-alert">{error}</div> : null}

        {resumeCandidate ? (
          <div className="parked-cart-review">
            <p className="eyebrow">CUSTOM REVIEW</p>
            <h3>พักบิลปัจจุบันก่อนเปิดบิลนี้?</h3>
            <p>บิลปัจจุบันมี {activeItemCount} รายการ ระบบจะพักไว้เป็นบิลใหม่ก่อน แล้วจึงเรียกบิลเวลา {formatParkedTime(resumeCandidate.parkedAt)} กลับมาขายต่อ โดยไม่รวมสองบิลเข้าด้วยกัน</p>
            <div className="parked-cart-review__actions">
              <button className="primary-action" type="button" disabled={busy} onClick={() => void confirmSwap()}>
                {busy ? "กำลังสลับบิล…" : "พักบิลปัจจุบัน แล้วเปิดบิลนี้"}
              </button>
              <button className="secondary-action" type="button" disabled={busy} onClick={() => setResumeCandidate(null)}>กลับ</button>
            </div>
          </div>
        ) : discardCandidate ? (
          <div className="parked-cart-review parked-cart-review--danger">
            <p className="eyebrow">CUSTOM REVIEW</p>
            <h3>ทิ้งบิลพักนี้?</h3>
            <p>บิลเวลา {formatParkedTime(discardCandidate.parkedAt)} มี {cartItemCount(discardCandidate.cart)} รายการ ยอด {formatMoney(cartTotal(discardCandidate.cart), cartCurrency(discardCandidate.cart))} การทิ้งจะลบ Local Draft นี้ออกจากเครื่องและไม่สร้างยอดขาย</p>
            <div className="parked-cart-review__actions">
              <button className="parked-cart-danger" type="button" disabled={busy} onClick={() => void confirmDiscard()}>
                {busy ? "กำลังทิ้ง…" : "ยืนยันทิ้งบิลพัก"}
              </button>
              <button className="secondary-action" type="button" disabled={busy} onClick={() => setDiscardCandidate(null)}>กลับ</button>
            </div>
          </div>
        ) : (
          <div className="parked-cart-list">
            {parked.map((entry, index) => (
              <article className="parked-cart-card" key={entry.parkedCartId}>
                <div className="parked-cart-card__main">
                  <div className="parked-cart-card__title">
                    <strong>พัก #{String(index + 1).padStart(2, "0")} · {formatParkedTime(entry.parkedAt)}</strong>
                    <span>{cartItemCount(entry.cart)} รายการ</span>
                  </div>
                  <p>{entry.cart.selectedMember?.customerName ?? "ไม่ระบุสมาชิก"}</p>
                  <small>พักโดย {entry.parkedBy.employeeName} ({entry.parkedBy.employeeCode})</small>
                </div>
                <strong className="parked-cart-card__amount">{formatMoney(cartTotal(entry.cart), cartCurrency(entry.cart))}</strong>
                <div className="parked-cart-card__actions">
                  <button className="primary-action" type="button" disabled={busy} onClick={() => void resumeParked(entry)}>กลับมาขายต่อ</button>
                  <button className="parked-cart-discard" type="button" disabled={busy} onClick={() => setDiscardCandidate(entry)}>ทิ้ง</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>,
    document.body,
  );

  return <>{actions}{modal}</>;
}
